import React from "react";
import {
  Popover,
  Box,
  Typography,
  Stack,
  Chip,
  Divider,
  Paper,
  Alert,
} from "@mui/material";
import {
  Factory as FactoryIcon,
  Block as BlockIcon,
} from "@mui/icons-material";
import { ProductBreakdownLine, LineType, AdditionalTariff } from "@/services/product-catalogue";
import { useGetDutyColumnsQuery, DutyColumn } from "@/services/product-catalogue/duty-columns-api";
import { Star as StarIcon } from "@mui/icons-material";
import { getAuthorityStyle } from "../rule-form-section/helpers";

interface TariffBreakdownPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  breakdownLines?: ProductBreakdownLine[];
  additionalTariffs?: AdditionalTariff[];
  totalRate: number;
  metalPercentage?: number;
  ftaApplicable?: boolean;
  ftaProgramName?: string;
  secondaryDutyRateString?: string | null;
}

const getLineTypeLabel = (lineType: LineType): string => {
  switch (lineType) {
    case LineType.METAL:
      return "Metal Portion";
    case LineType.NONMETAL:
      return "Non-Metal Portion";
    case LineType.FULL:
      return "Full Value";
    default:
      return lineType;
  }
};


const TariffBreakdownPopover: React.FC<TariffBreakdownPopoverProps> = ({
  anchorEl,
  open,
  onClose,
  breakdownLines,
  additionalTariffs,
  totalRate,
  metalPercentage,
  ftaApplicable,
  ftaProgramName,
  secondaryDutyRateString,
}) => {
  // Show popover if we have either breakdown lines or additional tariffs
  const hasBreakdown = breakdownLines && breakdownLines.length > 0;
  const hasTariffs = additionalTariffs && additionalTariffs.length > 0;
  const { data: dutyColumnsResp } = useGetDutyColumnsQuery({ pageNo: 1, pageSize: 500 } as any);
  const dutyColumns: DutyColumn[] = dutyColumnsResp?.data || [];
  const columnMap = new Map<string, DutyColumn>();
  dutyColumns.forEach(dc => dc && dc._id && columnMap.set(dc._id, dc));
  
  // Check if HTS General Duty is missing
  const hasGeneralDuty = breakdownLines?.some(line => 
    line.calculationBreakdown?.some(calc => 
      calc.applied && (
        calc.columnName.toLowerCase().includes('general duty') ||
        calc.columnName.toLowerCase().includes('hts') ||
        calc.columnId === 'general-duty'
      )
    )
  );
  
  // Collect unique column IDs present in applied breakdown entries (excluding HTS synthetic IDs)
  const usedColumnIds = new Set<string>();
  breakdownLines?.forEach(line => {
    line.calculationBreakdown?.forEach(calc => {
      if (calc.applied && typeof calc.columnId === 'string' && calc.columnId !== 'general-duty' && calc.columnId !== 'secondary-duty') {
        usedColumnIds.add(calc.columnId as string);
      }
    });
  });
  // Build footnotes list: only columns with citations
  const footnoteColumns = Array.from(usedColumnIds)
    .map(id => columnMap.get(id))
    .filter((dc): dc is DutyColumn => !!dc && Array.isArray((dc as any).citations) && (dc as any).citations.length > 0);
  
  if (!hasBreakdown && !hasTariffs) {
    return null;
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      PaperProps={{
        sx: {
          maxWidth: 600,
          minWidth: 400,
          p: 2,
          borderRadius: 2,
          boxShadow: 3,
        },
      }}
    >
      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={600}>
            Tariff Calculation Breakdown
          </Typography>
          <Chip
            label={`Total: ${(totalRate * 100).toFixed(2)}%`}
            color="primary"
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Stack>

        {ftaApplicable ? (
          <Alert severity="success" sx={{ py: 0.5 }}>
            <Typography variant="body2">
              Preferential origin applies{ftaProgramName ? ` under ${ftaProgramName}` : ''}. General duty may be exempt.
            </Typography>
          </Alert>
        ) : null}

        {metalPercentage !== undefined && metalPercentage > 0 && (
          <Alert severity="info" icon={<FactoryIcon />} sx={{ py: 0.5 }}>
            <Typography variant="body2">
              Product contains {metalPercentage}% metal content
            </Typography>
          </Alert>
        )}

        {!hasGeneralDuty && (
          <Alert severity="error" sx={{ py: 0.5 }}>
            <Typography variant="body2">
              HTS code not found in HTSUSITC database. Please edit and try a different code.
            </Typography>
          </Alert>
        )}

        <Divider />

        {/* Show breakdown lines if available */}
        {hasBreakdown ? (
          <>
            {/* Breakdown Lines */}
            {breakdownLines.map((line, index) => (
          <Paper
            key={index}
            elevation={0}
            sx={{
              p: 1.5,
              backgroundColor: "#FAFAFA",
              border: "1px solid #F3F4F6",
              borderRadius: "8px",
            }}
          >
            <Stack spacing={1.5}>
              {/* Line Header */}
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Chip
                    label={getLineTypeLabel(line.lineType) === "Metal Portion" ? "Metal" : getLineTypeLabel(line.lineType) === "Non-Metal Portion" ? "Non-Metal" : getLineTypeLabel(line.lineType)}
                    size="small"
                    sx={{
                      backgroundColor: line.lineType === LineType.METAL ? '#FFF7ED' : line.lineType === LineType.NONMETAL ? '#EFF6FF' : '#ECFDF5',
                      color: line.lineType === LineType.METAL ? '#C2410C' : line.lineType === LineType.NONMETAL ? '#1D4ED8' : '#047857',
                      fontWeight: 600,
                      borderRadius: '4px',
                      border: '1px solid transparent',
                      borderColor: line.lineType === LineType.METAL ? '#FFEDD5' : line.lineType === LineType.NONMETAL ? '#DBEAFE' : '#D1FAE5',
                    }}
                  />
                  {line.portion < 1 && (
                    <Typography variant="caption" color="text.secondary" fontFamily="Inter, sans-serif">
                      ({(line.portion * 100).toFixed(0)}% of value)
                    </Typography>
                  )}
                {line.exclusionsApplied && line.exclusionsApplied.length > 0 && (
                  <StarIcon sx={{ fontSize: 16, color: getAuthorityStyle(line.exclusionsApplied[0]?.authority).color }} />
                )}
                </Stack>
                <Typography variant="body2" fontWeight={600} fontFamily="Inter, sans-serif">
                  {(line.totalRate * 100).toFixed(2)}%
                </Typography>
              </Stack>
              <Divider sx={{ my: 1, borderColor: '#E5E7EB' }} />
              {/* Applicable Rules */}
              {line.applicableRules.length > 0 ? (
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 0.5, display: 'block' }} fontFamily="Inter, sans-serif">APPLIED RULES</Typography>
                  <Box component="ul" sx={{ pl: 2.5, my: 0 }}>
                    {line.applicableRules.map((rule, ruleIndex) => {
                      const excluded = (line.exclusionsApplied || []).some(e => e.columnId === rule.columnId);
                      const color = excluded ? getAuthorityStyle((line.exclusionsApplied || []).find(e => e.columnId === rule.columnId)?.authority).color : undefined;
                      // Render specific duty string for HTS secondary duty
                      if (rule.columnId === 'secondary-duty') {
                        const s = (secondaryDutyRateString || '').toString().trim();
                        if (!s || s.toLowerCase() === 'null') {
                          return null;
                        }
                        return (
                          <li key={ruleIndex}>
                            <Typography variant="body2" fontSize={13} fontFamily="Inter, sans-serif">
                              Specific Duty: {s}
                            </Typography>
                          </li>
                        );
                      }
                      return (
                        <li key={ruleIndex}>
                          <Typography variant="body2" fontSize={13} fontFamily="Inter, sans-serif">
                            {rule.columnName}: {(rule.rate * 100).toFixed(2)}%
                            {excluded && (
                              <>
                                <StarIcon sx={{ fontSize: 14, color, verticalAlign: 'middle', ml: 0.5 }} />
                                <span style={{ marginLeft: 4, color: '#059669', fontWeight: 500 }}>{"→ 0%"}</span>
                              </>
                            )}
                          </Typography>
                        </li>
                      );
                    })}
                  </Box>
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary" fontStyle="italic" fontFamily="Inter, sans-serif">No applicable duties on this portion.</Typography>
              )}

            </Stack>
          </Paper>
            ))}

            {/* Non-applicable duty types intentionally omitted */}

            {/* Exclusion Information */}
            {breakdownLines.some(line => 
              line.calculationBreakdown?.some(calc => calc.mutuallyExclusiveGroup)
            ) && (
              <Alert severity="warning" icon={<BlockIcon />} sx={{ py: 0.5 }}>
                <Typography variant="caption">
                  Some rules belong to mutually exclusive groups - only one rule per group can apply
                </Typography>
              </Alert>
            )}
          </>
        ) : (
          /* Simple tariff list when no breakdown available */
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Applied Duty Types:
            </Typography>
            {additionalTariffs && additionalTariffs
              .filter(tariff => tariff.rate > 0 || tariff.columnId === 'secondary-duty')
              .map((tariff, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    backgroundColor: "#f9fafb",
                    border: "1px solid #e5e7eb",
                    borderRadius: 1,
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="body2">
                      {tariff.columnId === 'secondary-duty' ? 'Specific Duty (HTS)' : tariff.columnName}
                    </Typography>
                    {tariff.columnId === 'secondary-duty' ? (() => {
                      const s = (secondaryDutyRateString || '').toString().trim();
                      if (!s || s.toLowerCase() === 'null') {
                        return null;
                      }
                      return (
                      <Chip
                        label={s}
                        size="small"
                        sx={{
                          backgroundColor: '#e0f2fe',
                          color: '#0369a1',
                          fontWeight: 600,
                        }}
                      />
                      );
                    })() : (
                    <Chip
                      label={`${(tariff.rate * 100).toFixed(2)}%`}
                      size="small"
                      sx={{
                        backgroundColor: '#e0f2fe',
                        color: '#0369a1',
                        fontWeight: 600,
                      }}
                    />
                    )}
                  </Stack>
                </Paper>
              ))}
            {additionalTariffs && additionalTariffs.filter(t => t.rate > 0).length === 0 && (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                No duties apply to this product
              </Typography>
            )}
          </Stack>
        )}
        
        {/* Citations Footnotes */}
        {footnoteColumns.length > 0 && (
          <>
            <Divider />
            <Paper elevation={0} sx={{ p: 1.5, border: "1px solid #E5E7EB", borderRadius: '8px', bgcolor: '#F9FAFB' }}>
              <Typography variant="subtitle2" fontSize={12} fontWeight={700} sx={{ mb: 1, textTransform: 'uppercase', color: '#4B5563' }}>Sources</Typography>
              <Stack spacing={1.5}>
                {footnoteColumns.map((dc: any, idx: number) => (
                  <Box key={dc._id}>
                    <Typography variant="body2" fontWeight={600} fontSize={13} color="#111827">
                      {idx + 1}. {dc.name}
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, my: 0.5 }}>
                      {(dc.citations || []).map((c: any, cidx: number) => {
                        return (
                          <li key={cidx}>
                            <Typography variant="body2" fontSize={13} sx={{ wordBreak: 'break-all' }}>
                              <a href={c.url} target="_blank" rel="noopener noreferrer" style={{ color: '#0B494B', textDecoration: 'none' }}>{c.url}</a>
                            </Typography>
                          </li>
                        );
                      })}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </>
        )}
        {/* Exclusions Legend */}
        {hasBreakdown && breakdownLines?.some(l => (l.exclusionsApplied || []).length > 0) && (
          <>
            <Divider />
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Exclusions
              </Typography>
              <Stack spacing={1}>
                {Array.from(
                  new Map(
                    breakdownLines!.flatMap(l => (l.exclusionsApplied || [])).map(e => [e.listId + e.columnId, e])
                  ).values()
                ).map((e, idx) => {
                  const color = getAuthorityStyle(e.authority).color;
                  return (
                    <Stack key={`${e.listId}-${e.columnId}-${idx}`} direction="row" spacing={1} alignItems="center">
                      <StarIcon sx={{ fontSize: 16, color }} />
                      <Typography variant="body2">
                        {e.listName}: {e.columnName} — {e.matchedCode}
                      </Typography>
                    </Stack>
                  )
                })}
              </Stack>
            </Box>
          </>
        )}
      </Stack>
    </Popover>
  );
};

export default TariffBreakdownPopover;